import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';

interface LeaveApplication {
  id: string;
  application_number: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  leave_type_name: string;
  from_date: string;
  to_date: string;
  number_of_days: number;
  status: string;
  cancellation_status: string | null;
}

interface PendingApproval {
  approvalInstanceId: string;
  application_number: string;
  leave_type_name: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  from_date: string;
  to_date: string;
  number_of_days: number;
  currentLevel: number;
  finalLevel: number;
  claim_amount_medical?: number;
  claim_amount_dental?: number;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchLeaveTypeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/lms/leave-types');
  const types = res.data.leaveTypes || [];
  const filtered = search
    ? types.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase()))
    : types;
  return filtered.map((t: any) => ({ value: t.id, label: `${t.name} (${t.code})` }));
};

const APPLICATION_TYPES = [
  { value: 'FULL_DAY', label: 'Full Day' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'CLAIM_AMOUNT_ONLY', label: 'Claim Amount Only (Medical/Dental)' },
];

const TABS = [
  { key: 'my-approvals', label: 'Pending for Me' },
  { key: 'my-decisions', label: 'My Approved/Rejected' },
  { key: 'pending-previous-level', label: 'Pending Previous Level' },
];

export default function LeaveApplications() {
  const [applications, setApplications] = useState<LeaveApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [approvalTab, setApprovalTab] = useState('my-approvals');
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<LeaveApplication | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [formData, setFormData] = useState({
    employeeId: '',
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    applicationType: 'FULL_DAY',
    halfDayType: 'FIRST_HALF',
    hoursApplied: 4,
    reason: '',
    comments: '',
    claimAmountMedical: '',
    claimAmountDental: '',
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    fetchApprovalTab(approvalTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalTab]);

  useEffect(() => {
    if (formData.employeeId && formData.leaveTypeId) {
      checkBalance();
    } else {
      setAvailableBalance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employeeId, formData.leaveTypeId]);

  const checkBalance = async () => {
    try {
      const res = await api.get(`/lms/leave-balances/${formData.employeeId}`);
      const balances = res.data.balances || [];
      const match = balances.find((b: any) => b.leave_type_id === formData.leaveTypeId);
      setAvailableBalance(match ? Number(match.closing_balance) : 0);
    } catch {
      setAvailableBalance(null);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/leave-applications');
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to fetch leave applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalTab = async (tab: string) => {
    try {
      if (tab === 'my-decisions') {
        const res = await api.get('/lms/leave-applications/my-decisions');
        setDecisions(res.data.applications || []);
        setPendingApprovals([]);
      } else {
        const res = await api.get(`/lms/leave-applications/${tab}`);
        setPendingApprovals(res.data.approvals || []);
        setDecisions([]);
      }
    } catch (error) {
      console.error('Failed to fetch approval tab:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = { ...formData };
      if (formData.applicationType !== 'HOURLY') delete payload.hoursApplied;
      if (formData.applicationType !== 'HALF_DAY') delete payload.halfDayType;
      if (formData.applicationType !== 'CLAIM_AMOUNT_ONLY') {
        delete payload.claimAmountMedical;
        delete payload.claimAmountDental;
      }
      await api.post('/lms/leave-applications', payload);
      setFormData({
        employeeId: '', leaveTypeId: '', fromDate: '', toDate: '', applicationType: 'FULL_DAY',
        halfDayType: 'FIRST_HALF', hoursApplied: 4, reason: '', comments: '', claimAmountMedical: '', claimAmountDental: '',
      });
      setShowForm(false);
      fetchApplications();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to submit leave application');
    }
  };

  const handleApprovalAction = async (applicationId: string, action: 'APPROVED' | 'REJECTED') => {
    try {
      await api.put(`/lms/leave-applications/${applicationId}/approve`, { action });
      fetchApprovalTab(approvalTab);
      fetchApplications();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to act on approval');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason) return;
    try {
      await api.put(`/lms/leave-applications/${cancelTarget.id}/cancel`, { reason: cancelReason });
      setCancelTarget(null);
      setCancelReason('');
      fetchApplications();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to cancel application');
    }
  };

  const columns: Column<LeaveApplication>[] = [
    { key: 'application_number', header: 'App No.' },
    { key: 'employee', header: 'Employee', render: (row) => `${row.first_name} ${row.last_name}` },
    { key: 'leave_type_name', header: 'Leave Type' },
    { key: 'from_date', header: 'From', render: (row) => new Date(row.from_date).toLocaleDateString() },
    { key: 'to_date', header: 'To', render: (row) => new Date(row.to_date).toLocaleDateString() },
    { key: 'number_of_days', header: 'Days' },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'cancellation_status', header: 'Cancellation',
      render: (row) => (row.cancellation_status ? <StatusBadge status={row.cancellation_status} /> : '—'),
    },
    {
      key: 'actions', header: 'Actions',
      render: (row) =>
        row.status === 'APPLIED' || row.status === 'APPROVED' ? (
          <button className="text-red-600 text-xs hover:underline" onClick={() => setCancelTarget(row)}>
            Cancel
          </button>
        ) : null,
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leave Applications</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Apply for Leave'}</Button>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setApprovalTab(t.key)}
              className={`px-4 py-3 text-sm font-medium ${approvalTab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-4">
          {approvalTab === 'my-decisions' ? (
            decisions.length === 0 ? (
              <p className="text-sm text-gray-500">No decisions yet.</p>
            ) : (
              <ul className="space-y-2">
                {decisions.map((d: any) => (
                  <li key={d.id} className="flex justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
                    <span>
                      {d.first_name} {d.last_name} ({d.employee_code}) — <strong>{d.leave_type_name}</strong>,{' '}
                      {new Date(d.from_date).toLocaleDateString()} to {new Date(d.to_date).toLocaleDateString()} ({d.number_of_days} days)
                    </span>
                    <StatusBadge status={d.action} />
                  </li>
                ))}
              </ul>
            )
          ) : pendingApprovals.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing here.</p>
          ) : (
            <ul className="space-y-2">
              {pendingApprovals.map((approval) => (
                <li key={approval.approvalInstanceId} className="flex justify-between items-center bg-amber-50 rounded-lg p-3 text-sm">
                  <span>
                    {approval.first_name} {approval.last_name} ({approval.employee_code}) —{' '}
                    <strong>{approval.leave_type_name}</strong>,{' '}
                    {new Date(approval.from_date).toLocaleDateString()} to{' '}
                    {new Date(approval.to_date).toLocaleDateString()} ({approval.number_of_days} days) —
                    level {approval.currentLevel}/{approval.finalLevel}
                  </span>
                  {approvalTab === 'my-approvals' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprovalAction(approval.approvalInstanceId, 'APPROVED')}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApprovalAction(approval.approvalInstanceId, 'REJECTED')}
                        className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <LookupSelect
                label="Employee"
                value={formData.employeeId}
                onChange={(value) => setFormData({ ...formData, employeeId: value })}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
                required
              />
              <LookupSelect
                label="Leave Type"
                value={formData.leaveTypeId}
                onChange={(value) => setFormData({ ...formData, leaveTypeId: value })}
                fetchOptions={fetchLeaveTypeOptions}
                placeholder="Search leave type..."
                required
              />
              <Input
                label="From Date"
                type="date"
                value={formData.fromDate}
                onChange={(e) => setFormData({ ...formData, fromDate: e.target.value })}
                required
              />
              <Input
                label="To Date"
                type="date"
                value={formData.toDate}
                onChange={(e) => setFormData({ ...formData, toDate: e.target.value })}
                required
              />
              <Select
                label="Application Type"
                value={formData.applicationType}
                onChange={(e) => setFormData({ ...formData, applicationType: e.target.value })}
                options={APPLICATION_TYPES}
              />
              {formData.applicationType === 'HALF_DAY' && (
                <Select
                  label="Half Day"
                  value={formData.halfDayType}
                  onChange={(e) => setFormData({ ...formData, halfDayType: e.target.value })}
                  options={[
                    { value: 'FIRST_HALF', label: 'First Half' },
                    { value: 'SECOND_HALF', label: 'Second Half' },
                  ]}
                />
              )}
              {formData.applicationType === 'HOURLY' && (
                <Input
                  label="Hours Applied"
                  type="number"
                  step="0.5"
                  value={formData.hoursApplied}
                  onChange={(e) => setFormData({ ...formData, hoursApplied: parseFloat(e.target.value) })}
                />
              )}
              {formData.applicationType === 'CLAIM_AMOUNT_ONLY' && (
                <>
                  <Input
                    label="Medical Claim Amount"
                    type="number" step="0.01"
                    value={formData.claimAmountMedical}
                    onChange={(e) => setFormData({ ...formData, claimAmountMedical: e.target.value })}
                  />
                  <Input
                    label="Dental Claim Amount"
                    type="number" step="0.01"
                    value={formData.claimAmountDental}
                    onChange={(e) => setFormData({ ...formData, claimAmountDental: e.target.value })}
                  />
                </>
              )}
              <Input
                label="Reason (max 200 chars)"
                maxLength={200}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="col-span-2"
              />
            </div>

            {availableBalance !== null && formData.applicationType !== 'CLAIM_AMOUNT_ONLY' && (
              <div className="rounded-lg p-3 text-sm bg-green-50 text-green-700">
                Available balance: {availableBalance} day(s)
              </div>
            )}

            <Button type="submit" className="w-full">
              Submit Leave Application
            </Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={applications} loading={loading} emptyMessage="No leave applications yet" />

      <Modal isOpen={!!cancelTarget} onClose={() => setCancelTarget(null)} title="Cancel Leave Application">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {cancelTarget?.status === 'APPROVED'
              ? 'This is an approved leave — cancellation requires approver sign-off.'
              : 'This pending application will be cancelled immediately.'}
          </p>
          <Input label="Cancellation Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} required />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelTarget(null)}>Close</Button>
            <Button onClick={handleCancel}>Submit Cancellation</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
